import { RoleRoute, RoleRouteObject } from '@/types'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import { defer, LoaderFunction, redirect } from 'react-router-typesafe'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import HoursPage from '@/routes/hours/HoursPage'
import HourEditorRoute from '@/routes/hours/HourEditor.route'
import HourCreatorRoute from '@/routes/hours/HourCreator.route'
import { Roles } from '@/auth/azure/Roles'
import RouterErrorHandler from '@/components/RouterErrorHandler'
import { isUserRoleAllowed } from '@/routes/utils'
import { MealVoucherPageRoute } from '@/routes/hours/meal-voucher/MealVoucherPage.route'
import { faClock } from '@fortawesome/free-solid-svg-icons'
import { HoursDashboardRoute } from '@/routes/hours/dashboard/HoursDashboard.route'
import { useUiStore } from '@/store/uiStore'
import { AnomaliesPageRoute } from '@/routes/hours/anomalies/AnomaliesPage.route'
import { Azienda } from '@/enums'

export const hoursPageRouteLoader = (async () => {
  if (useUiStore.getState().hoursView === 'dashboard') {
    return redirect('/workactivities/hours/dashboard')
  }
  const qsAdminApi = useQsAdminApiManager.getState().service
  const userInfo = await getAzureUserInformation()

  const getAziende = qsAdminApi.azienda().query((builder, azienda) => {
    builder.select('id', 'nome')
    builder.orderBy(azienda.nome.asc())
  })

  const getImpiegati = qsAdminApi.user().query((builder, impiegato) => {
    builder.select('id', 'fullname', 'email', 'manager')
    builder.expanding('manager', (managerBuilder, manager) => {
      managerBuilder.select('id', 'fullname', 'email')
    })
    builder.filter(impiegato.azienda.props.id.eq(Azienda.QS).and(impiegato.contractor.eq(false)))
    if (!isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.Administrator, Roles.Supervisor]))
      builder.filter(
        impiegato.email.eq(userInfo?.email ?? '').or(impiegato.manager?.props.email.eq(userInfo?.email ?? '')),
      )
    builder.orderBy(impiegato.fullname.asc())
  })

  return defer({
    title: 'Ore',
    aziende: await getAziende,
    impiegati: await getImpiegati,
    userInfo,
    defaultCRUDAllowedRoles: [
      Roles.GlobalAdministrator,
      Roles.Administrator,
      Roles.Supervisor,
      Roles.Sales,
      Roles.Marketing,
      Roles.TechDeveloper,
    ],
  })
}) satisfies LoaderFunction

export const HoursPageRoute: RoleRoute = {
  path: 'hours',
  children: [
    {
      index: true,
      element: <HoursPage />,
      loader: hoursPageRouteLoader,
      allowedRoles: [
        Roles.GlobalAdministrator,
        Roles.Administrator,
        Roles.Marketing,
        Roles.TechDeveloper,
        Roles.Sales,
        Roles.Supervisor,
      ],
    } as RoleRouteObject,
    HoursDashboardRoute,
    HourCreatorRoute,
    HourEditorRoute,
    MealVoucherPageRoute,
    AnomaliesPageRoute,
  ],
  errorElement: <RouterErrorHandler />,
  text: 'Ore',
  faIcon: faClock,
  handle: {
    crumb: () => ({
      label: 'Ore',
      icon: faClock,
    }),
  },
  allowedRoles: [
    Roles.GlobalAdministrator,
    Roles.Administrator,
    Roles.Marketing,
    Roles.TechDeveloper,
    Roles.Sales,
    Roles.Supervisor,
  ],
}
